import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef,
} from "react";
import {
  getPresidentList,
  getVicePresidentList,
  getRegions,
  getProvinces,
  getIndependentCities,
  getProvinceData,
} from "../../../../../api/ph-api";
import useSWRImmutable from "swr/immutable";
import { flatten } from "lodash";
import MapTransmissionChart from "../../../../../../components/races/national/map/MapTransmissionChart";
import MapChartLegend from "../../../../../../components/races/national/ChartLegend";
import { getTransmissionPercentage } from "../../../../../../helpers/util";
import styles from "../../../../../../styles/races/embed/Graph.module.scss";

export async function getRegionData(regionLink) {
  try {
    const res = await fetch(regionLink, {
      method: "GET",
      headers: {
        Authorization: process.env.API_TOKEN,
        "x-bypass-ratelimit": "pzQ!5eP7CWnUQSc</ojHpsd/gqfUJKgfKw=dH93po?fK",
      },
    });
    return await res.json();
  } catch (err) {
    return null;
  }
}

export const getStaticProps = async () => {
  const presidential = await getPresidentList();
  const vPresidential = await getVicePresidentList();
  const regionList = await getRegions();
  const provincesList = await getProvinces();
  const cityList = await getIndependentCities();
  const philippines = {
    slug: "philippines",
    name: "PH",
    display_name: "Philippines",
  };

  // Remove NCR from Provinces List
  const provincesNoNCR = provincesList.results.filter(
    (province) => province.slug !== "ncr"
  );
  provincesList.results = [...provincesNoNCR];
  provincesList.count = provincesList.count - 1;

  // Add candidateColor
  presidential.results.forEach((candidate) => {
    candidate.selected = true;
    candidate.votes = "0";
    candidate.percentage = 0;
    switch (candidate.last_name.toLowerCase()) {
      case "robredo":
        candidate.color = "rgba(252, 106, 223, 1)";
        candidate.legend = "rect";
        break;
      case "marcos":
        candidate.color = "rgba(192, 36, 2, 1)";
        candidate.legend = "rectRot";
        break;
      case "pacquiao":
        candidate.color = "rgba(57, 198, 240, 1)";
        candidate.legend = "circle";
        break;
      case "domagoso":
        candidate.color = "rgba(64, 118, 255, 1)";
        candidate.legend = "rectRot";
        break;
      case "de guzman":
        candidate.color = "rgba(242, 106, 90, 1)";
        candidate.legend = "rect";
        break;
      case "lacson":
        candidate.color = "rgba(183, 125, 222, 1)";
        candidate.legend = "circle";
        break;
      case "mangondato":
        candidate.color = "rgba(72, 167, 104, 1)";
        candidate.legend = "rect";
        break;
      case "montemayor":
        candidate.color = "rgba(202, 185, 36, 1)";
        candidate.legend = "triangle";
        break;
      case "abella":
        candidate.color = "rgba(92, 161, 144, 1)";
        candidate.legend = "circle";
        break;
      case "gonzales":
        candidate.color = "rgba(197, 101, 12, 1)";
        candidate.legend = "triangle";
        break;
      default:
        candidate.color = "rgba(0, 0, 0, 1)";
        candidate.legend = "circle";
        break;
    }
  });

  vPresidential.results.forEach((candidate) => {
    candidate.selected = true;
    candidate.votes = "0";
    candidate.percentage = 0;
    switch (candidate.last_name.toLowerCase()) {
      case "pangilinan":
        candidate.color = "rgba(252, 106, 223, 1)";
        candidate.legend = "rect";
        break;
      case "duterte":
        candidate.color = "rgba(192, 36, 2, 1)";
        candidate.legend = "rectRot";
        break;
      case "atienza":
        candidate.color = "rgba(57, 198, 240, 1)";
        candidate.legend = "circle";
        break;
      case "ong":
        candidate.color = "rgba(64, 118, 255, 1)";
        candidate.legend = "rectRot";
        break;
      case "bello":
        candidate.color = "rgba(242, 106, 90, 1)";
        candidate.legend = "rect";
        break;
      case "sotto":
        candidate.color = "rgba(183, 125, 222, 1)";
        candidate.legend = "circle";
        break;
      case "serapio":
        candidate.color = "rgba(72, 167, 104, 1)";
        candidate.legend = "rect";
        break;
      case "david":
        candidate.color = "rgba(202, 185, 36, 1)";
        candidate.legend = "triangle";
        break;
      case "lopez":
        candidate.color = "rgba(92, 161, 144, 1)";
        candidate.legend = "rectRot";
        break;
      default:
        candidate.color = "rgba(0, 0, 0, 1)";
        candidate.legend = "circle";
        break;
    }
  });

  // THIS SHOULD BE REPLACED BY AN API CALL
  const getTrStatusByLocation = async (locationSlug) => {
    return {
      votes: 0,
      percentage: 0,
      timestamp: "May 9 2022 17:00:00 GMT+0800",
    };
  };

  await Promise.all(
    cityList.cities.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;

      let provinceSlug = location.province.slug;
      const provinceData = await getProvinceData(provinceSlug);
      const regionData = await getRegionData(provinceData.region);
      location.region = regionData;
    })
  );

  await Promise.all(
    provincesList.results.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;

      let provinceSlug = location.slug;
      const provinceData = await getProvinceData(provinceSlug);
      const regionData = await getRegionData(provinceData.region);
      location.region = regionData;
    })
  );

  await Promise.all(
    regionList.results.map(async (location) => {
      const { votes, percentage, timestamp } = await getTrStatusByLocation(
        location.slug
      );
      location.votes = votes;
      location.percentage = percentage;
      location.timestamp = timestamp;
    })
  );

  const { votes, percentage, timestamp } = await getTrStatusByLocation(
    philippines.slug
  );
  philippines.votes = votes;
  philippines.percentage = percentage;
  philippines.timestamp = timestamp;

  const slugList = [
    ...provincesList.results,
    ...regionList.results,
    ...cityList.cities,
  ].map((region) => region.slug);
  slugList.push("philippines");
  const splitSlugLocation = [];
  while (slugList.length > 0) {
    splitSlugLocation.push(slugList.splice(0, 10));
  }
  const slugVicePresLocationRequest = [];
  splitSlugLocation.forEach((value) => {
    const requestVicePresident = fetch(`${cfUrl}/snapshotResults`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data: {
          locationSlugs: value.join(","),
          interval: 1000,
          raceType: "VICE PRESIDENT PHILIPPINES",
        },
      }),
    }).then(async (res) => {
      const x = await res.json();
      return x;
    });
    slugVicePresLocationRequest.push(requestVicePresident);
  });
  const slugVicePresLocationResult = await Promise.all(
    slugVicePresLocationRequest
  );

  let viceLocationData = [];
  viceLocationData = flatten(
    slugVicePresLocationResult.map((data) => data?.result)
  );

  if (
    !presidential ||
    !vPresidential ||
    !regionList ||
    !provincesList ||
    !cityList ||
    !viceLocationData
  ) {
    return {
      props: {
        presidential,
        vPresidential,
        regionList,
        provincesList,
        cityList,
        philippines,
        viceLocationData,
      },
      revalidate: 5,
    };
  }

  return {
    props: {
      presidential,
      vPresidential,
      regionList,
      provincesList,
      cityList,
      philippines,
      viceLocationData,
    },
  };
};

const reducer = (state, { type, slug, mutation }) => {
  if (type === "set") {
    return mutation;
  } else if (type === "update") {
    const candidateIndex = state.findIndex(
      (candidate) => candidate.wp_slug === slug
    );
    if (candidateIndex === -1) {
      return [...state];
    }

    state[candidateIndex] = {
      ...state[candidateIndex],
      ...mutation,
    };

    if (mutation.votes) {
      state.sort((a, b) => b.votes - a.votes);
    }

    return [...state];
  } else {
    return [...state];
  }
};

const cfUrl = process.env.API_CF_URL;
const EmbedPresidentTransmissionMap = ({
  presidential,
  vPresidential,
  regionList,
  provincesList,
  cityList,
  philippines,
  viceLocationData,
}) => {
  const ELECTION_DAY_TRANSMISSION_START = 1652094000000;
  const TOTAL_PRECINCTS_OAV = (
    regionList?.results.find((region) => region.slug === "oav") || {}
  ).precincts_count;
  // const TIMELAPSE_START_DATE = new Date(
  //   "May 9 2022 17:00:00 GMT+0800"
  // ).getTime();
  // const TIMELAPSE_END_DATE = new Date(
  //   "May 11 2022 18:00:00 GMT+0800"
  // ).getTime();

  const [presidentialList, setPresidentialList] = useReducer(reducer, [
    ...presidential.results,
  ]);
  const [vPresidentialList, setVPresidentialList] = useReducer(reducer, [
    ...vPresidential.results,
  ]);
  const [raceType, setRaceType] = useState("vice-presidential");
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [snapshotData, setSnapshotData] = useState([...viceLocationData]);
  const [transmissionState, setTransmissionState] = useState({
    labels: [],
    datasets: [{ label: "", data: null }],
  });
  const TOTAL_PRECINCTS_PH = regionList?.results
    ? (regionList?.results || [])
        .map((region) => (region || {}).precincts_count)
        .reduce((a, b) => a + b, 0)
    : null;
  const TIMELAPSE_INTERVAL_CHART = 60 * 60 * 1000; // 1 hour
  const [url, setUrl] = useState("");
  const slugList = [
    ...provincesList.results,
    ...regionList.results,
    ...cityList.cities,
  ].map((region) => region.slug);
  const _raceType = "VICE PRESIDENT PHILIPPINES";
  const fetcher = (url, body) =>
    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body,
    }).then(async (res) => {
      return res.json();
    });

  const {
    data: rawCumulativeVotesChartData,
    error: cumulativeVotesChartError,
  } = useSWRImmutable(
    [
      `${cfUrl}/PHCumulativeVotes`,
      JSON.stringify({
        data: {
          raceType: _raceType,
        },
      }),
    ],
    fetcher,
    {
      refreshInterval: 0,
      shouldRetryOnError: true,
      revalidateOnReconnect: true,
      refreshWhenOffline: true,
      errorRetryInterval: 5000,
      errorRetryCount: 1,
    }
  );
  const cumulativeVotesChartData = rawCumulativeVotesChartData?.result;

  const transmissionStatusData = () => {
    const labels = [];
    const datasets = [];

    if (snapshotData && snapshotData.length) {
      let candidateList = [...presidentialList];
      if (raceType === "vice-presidential") {
        candidateList = [...vPresidentialList];
      }

      const locationList = [...regionList.results];
      if (selectedRegion) {
        locationList = [...provincesList.results, ...cityList.cities];
        locationList = locationList.filter(
          (location) => location.region.slug === selectedRegion.slug
        );
        locationList.sort((a, b) => {
          if (a.display_name < b.display_name) {
            return -1;
          }
          if (a.display_name > b.display_name) {
            return 1;
          }

          return 0;
        });
      }

      let phDataList = [
        {
          timestamp: new Date().getTime(),
          transmissionCount: 0,
        },
      ];
      phDataList = [
        ...phDataList,
        ...snapshotData.filter((data) => data.locationSlug === "philippines"),
      ];

      // handling for test data: start 15 minutes before first transmission timestamp
      if (phDataList[1] && phDataList[1].timestamp) {
        phDataList[0].timestamp =
          phDataList[1].timestamp < ELECTION_DAY_TRANSMISSION_START
            ? new Date(phDataList[1].timestamp - 60 * 1000 * 15)
            : new Date(ELECTION_DAY_TRANSMISSION_START);
      }

      locationList.forEach((location) => {
        const data = [];
        const transmissionPercentage = [];
        let leadingCandidate = { color: "#172038", legend: "circle" };
        const filteredLocationData = snapshotData.filter(
          (item) =>
            item.locationSlug.toLowerCase() === location.slug.toLowerCase()
        );
        phDataList.forEach((phData, index) => {
          const locationTransmissionData = filteredLocationData.find(
            (locData) => locData.timestamp === phData.timestamp
          );
          if (locationTransmissionData) {
            leadingCandidate = candidateList.find(
              (candidate) =>
                candidate.wp_slug.toLowerCase() ===
                filteredLocationData[
                  filteredLocationData.length - 1
                ].leadingCandidateSlug.toLowerCase()
            );

            transmissionPercentage.push(
              getTransmissionPercentage(
                phData.transmissionCount,
                TOTAL_PRECINCTS_PH
              )
            );
            data.push({
              x: new Date(locationTransmissionData.timestamp),
              y: getTransmissionPercentage(
                locationTransmissionData.transmissionCount,
                location.precincts_count
              ),
            });
          } else {
            if (index === 0) {
              transmissionPercentage.push("0");
              data.push({
                x:
                  phData.timestamp < ELECTION_DAY_TRANSMISSION_START
                    ? new Date(phData.timestamp)
                    : new Date(ELECTION_DAY_TRANSMISSION_START),
                y: "0",
              });
            } else {
              data.push(null);
            }
          }
        });

        datasets.push({
          label: location.name,
          data,
          transmissionPercentage,
          locationColor: leadingCandidate.color,
          borderColor: leadingCandidate.color,
          borderWidth: 1,
          backgroundColor: leadingCandidate.color,
          pointStyle: leadingCandidate.legend,
          pointBorderColor: leadingCandidate.color,
          pointBorderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          hitRadius: 5,
        });
      });
    } else {
      datasets.push({
        label: "",
        data: null,
      });
    }

    setTransmissionState({
      labels,
      datasets,
    });
  };

  useEffect(() => {
    if (snapshotData && snapshotData.length && cumulativeVotesChartData) {
      transmissionStatusData();
    }
  }, [snapshotData, cumulativeVotesChartData]);

  return (
    <div className={styles.mapResultsContainer} id="map-results-container">
      <div className={styles.content}>
        <div
          data-id="map-ranking-container"
          className={`${styles.rankingContainer}`}
        >
          <div className={styles.chartContainer}>
            <MapTransmissionChart
              dataChart={transmissionState}
              place="Region"
            />
          </div>
          <MapChartLegend candidatesList={vPresidential.results} />
        </div>
      </div>
    </div>
  );
};

export default EmbedPresidentTransmissionMap;
